/**
 * Creating an assessment from the sheet.
 *
 * The Assessments page is still where an assessment is *edited*, but adding one
 * shouldn't cost a round trip: "+" on the grid collects exactly the same four
 * properties in place, then drops the column into the sheet ready to type into.
 *
 * This is also the ONLY place a K/T/C/A split can be created. The shape is
 * hard-locked at creation - `PUT /assessments/:id` returns 400 for either
 * direction, because turning a single into a split leaves one number with no
 * section to belong to, and collapsing a split throws away the breakdown the
 * teacher recorded. So the choice is a first-class step here, and the lock is
 * stated - in ONE line. (An earlier layout said all of this in card blurbs, a
 * three-line warning and two footnotes; the modal read as a form with homework
 * attached, and none of it survived a usability pass.)
 */

import { useState } from "react";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
/* One source of truth for the achievement chart. These were briefly duplicated
 * here while the grading core landed in parallel; a second copy of the four
 * category names is exactly the sort of thing that drifts out of step with the
 * sheet and the report card, so it collapsed back to the import. */
import { SECTIONS, SECTION_MAX_FIELD, SECTION_NAME } from "@/gradebook/grading";
import { DatePicker } from "@/gradebook/DatePicker";

/** Same field treatment as the Assessments table, so the two agree on sight. */
const FIELD = [
  "h-9 w-full border border-border bg-background px-2 text-[13px] text-foreground",
  "outline-none transition-colors focus:border-primary",
].join(" ");

const LABEL = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";

/* The section maxes are Decimal(6,2) and the API rejects a `max` that isn't the
 * sum of them, so the sum goes over the wire rounded to the same two places -
 * 20.1 + 15.2 must not arrive as 35.300000000000004 and fail the check. */
const round2 = (n) => Math.round(n * 100) / 100;

/** A blank box is "not typed yet", never zero - `0` is not a legal denominator. */
const num = (v) => (String(v).trim() === "" ? null : Number(v));
const positive = (v) => {
  const n = num(v);
  return n != null && Number.isFinite(n) && n > 0;
};

export default function NewAssessmentModal({ categories, defaultDate, onCreate, onClose }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(defaultDate);
  const [max, setMax] = useState(100);
  const [categoryId, setCategoryId] = useState("");
  const [split, setSplit] = useState(false);

  /* On = this task assesses that section. Deliberately NOT pre-filled with
   * 25/25/25/25: an equal raw split is wrong for most real tests, and a default
   * that is usually wrong is one a hurried teacher accepts without reading. An
   * empty box asks the question; a filled one answers it badly. */
  const [sections, setSections] = useState(
    () => Object.fromEntries(SECTIONS.map((k) => [k, { on: true, max: "" }])),
  );

  const patchSection = (key, changes) =>
    setSections((s) => ({ ...s, [key]: { ...s[key], ...changes } }));

  const ticked = SECTIONS.filter((k) => sections[k].on);
  /* Only ON rows count toward the total. An off section is "not assessed" - it
   * has no denominator at all, which is a different claim from a zero, and a
   * zero would drag the total down. */
  const sectionTotal = round2(
    ticked.reduce((sum, k) => sum + (num(sections[k].max) ?? 0), 0),
  );

  const titled = title.trim().length > 0;
  const sectionsFilled = ticked.length > 0 && ticked.every((k) => positive(sections[k].max));
  const valid = titled && (split ? sectionsFilled : positive(max));

  /* Said out loud under the button rather than left as a disabled control with
   * no explanation - "why can't I save this" is the question a greyed-out
   * button always raises. */
  const blocker = (() => {
    if (!titled) return "Name the assessment to continue.";
    if (!split) return positive(max) ? null : "Enter what the assessment is out of.";
    if (ticked.length === 0) return "Turn on at least one section this task assesses.";
    if (!sectionsFilled) return "Every section that is on needs a mark total.";
    return null;
  })();

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;

    /* On a split `max` is DERIVED, never asked for twice: the API 400s when the
     * two disagree, and a teacher who typed both would be the one to discover
     * that. The sum is the only number that can be right. */
    const body = {
      // No client-side id: Postgres assigns the UUID, and the sheet reloads to
      // pick up the real column.
      title: title.trim(),
      date,
      max: split ? sectionTotal : Number(max),
      categoryId: categoryId || null,
      share: 1,
    };

    /* Only the ON sections are sent. An omitted max IS "not assessed" - there
     * is no flag and no zero-weight case - so leaving the key off is the whole
     * mechanism, not a shortcut. */
    if (split) {
      for (const k of ticked) body[SECTION_MAX_FIELD[k]] = num(sections[k].max);
    }

    onCreate(body);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6">
      <form
        onSubmit={submit}
        className="flex max-h-[88vh] w-full max-w-[420px] flex-col rounded-xl border border-border bg-card shadow-card"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="font-display text-xl leading-tight text-foreground">New Assessment</h2>
          <button
            type="button"
            onClick={onClose}
            title="Close without saving"
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {/* No label: the placeholder says it, and it is the first thing the
              autofocused caret is already sitting in. */}
          <input
            value={title}
            autoFocus
            placeholder="Assessment name"
            aria-label="Assessment name"
            onChange={(e) => setTitle(e.target.value)}
            className={cn(FIELD, "font-medium")}
          />

          {/* The shape choice sits ABOVE the denominator, because it decides
              what that box even is. A segmented switch, not two explained
              cards - the split option names itself, and the one thing worth a
              sentence is the lock. */}
          <div>
            <span className={LABEL}>Marked as</span>
            <div className="flex rounded-md border border-border bg-muted/40 p-0.5">
              {[
                [false, "Single Mark"],
                [true, "Split by K/T/C/A"],
              ].map(([value, label]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setSplit(value)}
                  aria-pressed={split === value}
                  className={cn(
                    "h-7 flex-1 rounded-[5px] text-[12.5px] font-medium transition-colors",
                    split === value
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {split && (
            <div>
              {/* Four chips, all ON to start - a real split usually assesses
                  all four, so the default should be the common case and
                  switching one OFF the deliberate act. The check/x glyph is
                  the affordance saying a chip is a toggle at all: four green
                  boxes alone read as headers, not switches. An off section is
                  left out of the total, not marked zero. */}
              <div className="grid grid-cols-4 gap-2">
                {SECTIONS.map((k) => {
                  const row = sections[k];
                  return (
                    <div key={k} className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => patchSection(k, { on: !row.on })}
                        title={`${SECTION_NAME[k]} - click to ${row.on ? "exclude" : "include"}`}
                        aria-pressed={row.on}
                        aria-label={`${SECTION_NAME[k]}${row.on ? ", assessed" : ", not assessed"}`}
                        className={cn(
                          "flex h-7 items-center justify-center gap-1 rounded-md border text-[12.5px] font-semibold transition-colors",
                          row.on
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-dashed border-muted-foreground/40 bg-background text-muted-foreground/60 hover:border-primary/60",
                        )}
                      >
                        {row.on ? <Check size={12} strokeWidth={3} /> : <X size={12} />}
                        {k}
                      </button>
                      {/* Disabled rather than absent, so toggling a section
                          never reshuffles the block. */}
                      <input
                        value={row.max}
                        disabled={!row.on}
                        inputMode="decimal"
                        aria-label={`${SECTION_NAME[k]} is out of`}
                        placeholder={row.on ? "0" : "-"}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => patchSection(k, { max: e.target.value })}
                        className={cn(
                          "h-8 w-full border px-2 text-center text-[13px] tabular-nums outline-none transition-colors",
                          row.on
                            ? "border-border bg-background text-foreground focus:border-primary"
                            : "border-transparent bg-muted/40 text-muted-foreground/40",
                        )}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Out of on its own, centred, and small - it is a 2-3 digit number,
              and a full-width box made it look like it wanted a sentence. On a
              split it is DERIVED (the API 400s when the sum and max disagree),
              shown read-only in the same slot so the sections above are
              visibly feeding the number the sheet will divide by. */}
          <div className="text-center">
            <span className={LABEL}>Out of</span>
            {split ? (
              <output
                className={cn(
                  "mx-auto flex h-9 w-16 items-center justify-center border border-border bg-muted/50 text-[14px] font-semibold tabular-nums",
                  sectionTotal > 0 ? "text-foreground" : "text-muted-foreground/60",
                )}
              >
                {sectionTotal > 0 ? sectionTotal : "-"}
              </output>
            ) : (
              <input
                id="na-max"
                value={max}
                inputMode="numeric"
                aria-label="Out of"
                onFocus={(e) => e.target.select()}
                onChange={(e) => setMax(e.target.value)}
                className="mx-auto block h-9 w-16 border border-border bg-background text-center text-[14px] tabular-nums text-foreground outline-none transition-colors focus:border-primary"
              />
            )}
          </div>

          {/* The bookkeeping pair, last: name and shape decide what this IS,
              date and category only decide where it files. */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className={LABEL}>Date</span>
              {/* Picker-only - see DatePicker for why this is not a native
                  date input. */}
              <DatePicker
                value={date}
                onChange={setDate}
                ariaLabel="Assessment date"
                buttonClassName={cn(FIELD, "text-left")}
              />
            </div>
            <div>
              <label className={LABEL} htmlFor="na-cat">Category</label>
              {/* No category is how a diagnostic is excluded - there is no
                  separate "doesn't count" flag to find. */}
              <select
                id="na-cat"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className={FIELD}
              >
                <option value="">Not counted</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-4">
          {/* The app's alert red: this line is what stands between the teacher
              and the disabled button, not a caption. */}
          <p className="min-w-0 text-[11.5px] font-medium leading-snug text-destructive">{blocker}</p>
          <div className="flex shrink-0 items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" disabled={!valid}>Add Assessment</Button>
          </div>
        </div>
      </form>
    </div>
  );
}
